import React from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';

/**
 * Auth gate — redirects to /login if the user is not authenticated.
 * Stores the attempted URL in location.state.from for post-login redirect.
 *
 * Usage in routes:
 *   <Route element={<PrivateRoute />}>
 *     <Route path="/dashboard" element={<DashboardPage />} />
 *   </Route>
 *
 * Or wrap a layout:
 *   <Route element={<PrivateRoute><AppLayout /></PrivateRoute>}>
 */
export default function PrivateRoute({ children }) {
  const { isAuthenticated, isLoadingAuth, user } = useAuth();
  const location = useLocation();

  if (isLoadingAuth) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-blue-600 border-t-transparent" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  // Rule 70 — federated-registered users land with status='pending_profile'
  // and MUST complete their profile (canonical username + real email if the
  // provider didn't supply one) before reaching any other authenticated
  // surface. The guard is loose on `/profile/complete` itself so the page
  // can render; everything else 302's here.
  if (user?.status === 'pending_profile' && location.pathname !== '/profile/complete') {
    return <Navigate to="/profile/complete" replace />;
  }

  return children ? <>{children}</> : <Outlet />;
}
